import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

export const api = axios.create({ 
  baseURL: API,
  // Disable browser caching for all API calls
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
});

api.interceptors.request.use((config) => {
  // Read token from sessionStorage (NOT localStorage)
  const t = sessionStorage.getItem("jc_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  
  // Add timestamp to prevent caching
  config.params = {
    ...config.params,
    _t: Date.now()
  };
  
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    // Solo redirect al login per errori di autenticazione reali (401)
    // Non per rate limiting (429) o altri errori
    if (status === 401) {
      // Verifica che non sia un errore di rate limiting mascherato
      const detail = error?.response?.data?.detail || '';
      if (!detail.toLowerCase().includes('rate') && !detail.toLowerCase().includes('limit')) {
        // Clear all storage
        try { 
          localStorage.clear();
          sessionStorage.clear();
          // Preserve app version
          localStorage.setItem("servicego_app_version", "2.0.0");
        } catch {}
        
        const current = window.location.pathname + window.location.search;
        if (!current.startsWith('/login')) {
          window.location.replace('/login');
        }
      }
    }
    return Promise.reject(error);
  }
);

export const login = (email, password) => api.post("/auth/login", { email, password });
export const register = (payload) => api.post("/auth/register", payload);
export const cancelPendingRegistration = () => api.post("/auth/cancel-pending-registration");
export const me = () => api.get("/auth/me");
export const updateProfile = (payload) => api.post("/auth/update-profile", payload);
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email });
export const resetPassword = (token, newPassword) => api.post("/auth/reset-password", { token, new_password: newPassword });
export const sendVerification = () => api.post("/auth/send-verification");
export const verifyEmail = (token) => api.post("/auth/verify-email", { token });
export const listCategories = () => api.get("/categories");
export const seed = () => api.post("/admin/seed");
export const createRequest = (payload) => api.post("/requests", payload);
export const myRequests = () => api.get("/requests/mine");
export const openRequests = (params) => api.get("/company/requests/open", { params });
export const createOffer = (payload) => api.post("/offers", payload);
export const offersByRequest = (id) => api.get(`/offers/by-request/${id}`);
export const chooseOffer = (id, offerId) => api.post(`/requests/${id}/choose`, { offerId });
export const myOffers = () => api.get("/company/offers/mine");
export const offersSummary = () => api.get("/company/offers/summary");
export const withdrawOffer = (id) => api.post(`/offers/${id}/withdraw`);
export const myInvoices = () => api.get("/invoices/mine");
export const billingCheckout = () => api.post("/billing/create-checkout-session", {});
export const billingPortal = () => api.post("/billing/portal-session", {});
export const createCheckoutSession = (packageId, metadata = {}) => api.post("/payments/checkout/session", {
  package_id: packageId,
  origin_url: window.location.origin,
  metadata
});
export const getCheckoutStatus = (sessionId) => api.get(`/payments/checkout/status/${sessionId}`);
export const unlockConversation = (requestId) => api.post(`/conversations/${requestId}/unlock`);
export const completeUnlock = (requestId) => api.post(`/conversations/${requestId}/complete-unlock`);
export const clientConversations = () => api.get("/client/conversations");
export const companyConversations = () => api.get("/company/conversations");
export const getCompanyRating = (companyId) => api.get(`/company/${companyId}/rating`);

// Admin
export const adminGetUsers = (search = '') => api.get(`/admin/users?limit=500${search ? `&search=${encodeURIComponent(search)}` : ''}`);
export const adminChangePassword = (userId, newPassword) => api.put(`/admin/users/${userId}/password`, { new_password: newPassword });
export const adminDeleteUser = (userId) => api.delete(`/admin/users/${userId}`);

// GDPR
export const exportUserData = () => api.get("/user/export-data");
export const deleteUserAccount = () => api.delete("/user/delete-account");

export default API;
